/* eslint-disable */
import Vue from "vue";
import Vuex from "vuex";
import router from "@/router";
import * as jwt from "@/services/jwt";
import JwtDecode from "jwt-decode";
import store from "store";
import { CHANGE_SETTING } from "@/store/settings";
import { i18n } from "@/localization";
import { clearAccessSuspension } from "@/services/accessSuspension";

const mapAuthProviders = {
  jwt: {
    login: jwt.login,
    qrLogin: jwt.qrLogin,
    currentAccount: jwt.currentAccount,
    logout: jwt.logout,
  },
};

Vue.use(Vuex);
export default {
  namespaced: true,
  state: {
    _id: "",
    userName: "",
    building: [],
    role: "",
    roleId: "",
    status: "",
    access: {},
    avatar: "",
    authorized: /* process.env.VUE_APP_AUTHENTICATED || */ false, // false is default value
    loading: false,
  },
  mutations: {
    SET_STATE(state, payload) {
      Object.assign(state, {
        ...payload,
      });
    },
    SET_BUILDING(state, payload) {
      state.building = payload;
    },

    SET_AVATAR(state, payload) {
      state.avatar = payload;
    },
  },
  actions: {
    LOGIN({ commit, dispatch, rootState }, { payload }) {
      return new Promise((resolve, reject) => {
        const { userName, password } = payload;
        commit("SET_STATE", {
          loading: true,
        });

        const login = mapAuthProviders[rootState.settings.authProvider].login;
        login(userName, password)
          .then(async (success) => {
            if (success) {
              // Check if MFA is required (response contains mfaToken)
              if (success.mfaToken) {
                // Return MFA data to login component
                const mfaData = {
                  mfaToken: success.mfaToken,
                  mfaMethod: success.mfaMethod,
                  phone: success.phone,
                  userId: success.userId,
                };

                // Include warning if present (e.g., SMS rate limit)
                if (success.warning) {
                  mfaData.warning = success.warning;
                }

                resolve(mfaData);
                return;
              }

              // Normal login flow
              Vue.prototype.$notification.success({
                message: i18n.t("login.connecte"),
                description: i18n.t("login.loginSuccess"),
              });
              if (success.info.building.length > 0) {
                await dispatch("settings/CHANGE_SETTING", {
                  setting: "activeBuilding",
                  value: success.info.building[0].dbName,
                }, { root: true });
              }
              const { building, role, status, userName, avatar, edtrustAdmin, _id } =
                success.info;
              const resourcesUrl = success.resourcesUrl;

              dispatch("settings/SET_RESOURCES_URL", resourcesUrl, {
                root: true,
              });
              commit("SET_STATE", {
                _id: _id,
                userName: userName,
                building: building,
                status: status,
                role: role.role,
                roleId: role._id,
                access: role.access,
                avatar: avatar,
                authorized: true,
                edtrustAdmin: edtrustAdmin,
              });
              resolve(success);
            }
            if (!success) {
              reject();
            }
          })
          .finally(() => {
            commit("SET_STATE", {
              loading: false,
            });
          });
      });
    },
    QR_LOGIN({ commit, dispatch, rootState }, { payload }) {
      return new Promise((resolve, reject) => {
        const { token } = payload;
        commit("SET_STATE", { loading: true });

        const qrLogin = mapAuthProviders[rootState.settings.authProvider].qrLogin;
        qrLogin(token)
          .then(async (success) => {
            if (success) {
              if (success.mfaToken) {
                const mfaData = {
                  mfaToken: success.mfaToken,
                  mfaMethod: success.mfaMethod,
                  phone: success.phone,
                  userId: success.userId,
                };
                if (success.warning) {
                  mfaData.warning = success.warning;
                }
                resolve(mfaData);
                return;
              }

              Vue.prototype.$notification.success({
                message: i18n.t("login.connecte"),
                description: i18n.t("login.loginSuccess"),
              });
              if (success.info.building.length > 0) {
                await dispatch(
                  "settings/CHANGE_SETTING",
                  {
                    setting: "activeBuilding",
                    value: success.info.building[0].dbName,
                  },
                  { root: true }
                );
              }
              const {
                building,
                role,
                status,
                userName,
                avatar,
                edtrustAdmin,
                _id,
              } = success.info;
              const resourcesUrl = success.resourcesUrl;

              dispatch("settings/SET_RESOURCES_URL", resourcesUrl, {
                root: true,
              });
              commit("SET_STATE", {
                _id: _id,
                userName: userName,
                building: building,
                status: status,
                role: role.role,
                roleId: role._id,
                access: role.access,
                avatar: avatar,
                authorized: true,
                edtrustAdmin: edtrustAdmin,
              });
              resolve(success);
            }
            if (!success) {
              reject();
            }
          })
          .catch(reject)
          .finally(() => {
            commit("SET_STATE", { loading: false });
          });
      });
    },
    SSO_LOGIN({ commit, dispatch, rootState }, { payload }) {
      return new Promise((resolve, reject) => {
        const { token, userId } = payload;
        commit("SET_STATE", {
          loading: true,
        });

        jwt
          .ssoLogin(token, userId)
          .then(async (success) => {
            if (success) {
              Vue.prototype.$notification.success({
                message: i18n.t("login.connecte"),
                description: i18n.t("login.loginSuccess"),
              });
              if (success.building.length > 0) {
                await dispatch("settings/CHANGE_SETTING", {
                  setting: "activeBuilding",
                  value: success.building[0].dbName,
                }, { root: true });
              }
              const { building, role, status, userName, avatar, edtrustAdmin, _id } =
                success;
              commit("SET_STATE", {
                _id: _id,
                userName: userName,
                building: building,
                status: status,
                role: role.role,
                roleId: role._id,
                access: role.access,
                avatar: avatar,
                authorized: true,
                edtrustAdmin: edtrustAdmin,
              });
              resolve(success);
            }
            if (!success) {
              reject();
            }
          })
          .finally(() => {
            commit("SET_STATE", {
              loading: false,
            });
          });
      });
    },

    TryAutoLogin({ dispatch, commit, rootState }, payload) {
      return new Promise((resolve, reject) => {
        // Access token is stored in memory, so it won't persist across page refreshes
        // This is by design - we rely on the refresh token (HTTP-only cookie) to get a new access token
        // If there's no activeBuilding, we can't verify, so logout
        if (!rootState.settings.activeBuilding) {
          dispatch("LOGOUT");
          reject(new Error("No active building"));
          return;
        }

        // Helper function to handle successful auto login
        const handleSuccessfulAutoLogin = (data) => {
          const {
            building,
            role,
            status,
            userName,
            avatar,
            edtrustAdmin,
          } = data.info;

          // If building is deactivated while user is connected, logout
          if (
            rootState.settings.activeBuildingId &&
            !building.find(
              (b) => b._id == rootState.settings.activeBuildingId
            )
          ) {
            dispatch("LOGOUT");
            Vue.prototype.$notification.error({
              message: "Problème de connexion",
              description: "Votre compte n'est plus actif",
            });
            reject(new Error("Building deactivated"));
            return;
          }

          dispatch("settings/SET_RESOURCES_URL", data.resourcesUrl, {
            root: true,
          });

          // Update school logo on auto login (if logo changes while user is connected)
          dispatch("settings/SET_IMAGE", building.map((b) => {
            return {
              name: b.name,
              db: b.dbName,
              dbName: b.dbName,
              headMaster: b.headMaster,
              logo: b.logo,
            };
          }),
            {
              root: true,
            }
          );

          commit("SET_STATE", {
            userName: userName,
            building: building,
            status: status,
            role: role.role,
            roleId: role._id,
            access: role.access,
            avatar: avatar,
            authorized: true,
            edtrustAdmin: edtrustAdmin,
          });

          resolve(data);
        };

        try {
          // Since access token is in memory (cleared on page refresh),
          // we always rely on the refresh token (HTTP-only cookie) to get a new access token
          // The verify endpoint will use the refresh token automatically via cookie
          const currentAccount =
            mapAuthProviders[rootState.settings.authProvider].currentAccount;

          // This will:
          // 1. Use refresh token from HTTP-only cookie to get new access token
          // 2. Store new access token in memory
          // 3. Return user info
          currentAccount(rootState.settings.activeBuilding).then((data) => {
            if (data && data.info) {
              handleSuccessfulAutoLogin(data);
            } else {
              dispatch("LOGOUT");
              reject(new Error("No user data"));
            }
          }).catch((error) => {
            // Refresh failed - no valid refresh token, redirect to login
            dispatch("LOGOUT");
            reject(error);
          });
        } catch (error) {
          console.error("Auto login error:", error);
          dispatch("LOGOUT");
          reject(error);
        }
      });
    },
    LOGOUT({ dispatch, commit, rootState }, sso = false) {
      const logout = mapAuthProviders[rootState.settings.authProvider].logout;
      logout().then(() => {
        clearAccessSuspension();
        dispatch("settings/RESET_SETTINGS", {}, { root: true });
        commit("SET_STATE", {
          userName: "",
          building: [],
          role: "",
          roleId: "",
          status: "",
          access: {},
          avatar: "",
          authorized: false,
          loading: false,
        });
        if (!sso) router.push("/auth/login");
      });
    },
    setAvatar({ commit }, payload) {
      commit("SET_AVATAR", payload);
    },
  },
  getters: {
    user: (state) => state,
    building: (state) => state.building,
  },
};
